import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  canChangeContactOwner,
  financingVisibleForRole,
  formatInteger,
  lifecycleLabels,
  lifecycleTones,
  type ContactBoardColumn,
  type ContactListSort,
  type LifecycleKey,
} from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import {
  Avatar,
  Banner,
  Button,
  Card,
  Checkbox,
  FilterChip,
  Pill,
  SearchField,
  Segmented,
  Select,
  Table,
  TableSkeleton,
  useToast,
  type TableColumn,
} from '../../ds/components';
import { useIsCompact, useIsMobile } from '../../ds/hooks';
import { errorMessage } from '../../lib/api';
import { usePermissions, useWorkspace } from '../../lib/session';
import { ModuleEmptyPage } from '../../shell/ModulePages';
import { AssignDialog } from './AssignDialog';
import {
  bulkContacts,
  exportContacts,
  useContactBoard,
  useContactCounts,
  useContactList,
  useReferrerPartnerOptions,
  useReferrerSubmitterOptions,
} from './api';
import { ContactsBoard } from './ContactsBoard';
import { CreateContactDialog } from './CreateContactDialog';
import { EntryCell, EntryHeader } from './EntryColumn';
import { FinancingCell, FinancingHeader } from './FinancingColumn';
import { EntryRangeFilter } from './EntryRangeFilter';
import { LifecycleDialog, type LifecycleSubject, type LifecycleTarget } from './LifecycleDialog';
import { SORT_OPTIONS, readStoredSort, storeSort } from './list-controls';
import { knownFromRef } from './users';
import type { BoardCard, BulkResult, ContactListItem } from './types';
import styles from './ContactsPage.module.css';

/** Order of the filter pills (Kontakte 01); "No Show" appears once contacts carry it. */
const PILL_ORDER: readonly LifecycleKey[] = [
  'new',
  'contacted',
  'call_booked',
  'prospect',
  'follow_up',
  'customer',
  'existing_customer',
  'not_reached',
  'no_show',
  'no_interest',
  'disqualified',
];

/** Every column takes a drop (D-333); the dialog opens with the column's stage preselected. */
const BOARD_TARGETS: Record<ContactBoardColumn, LifecycleTarget> = {
  new: 'new',
  contacted: 'contacted',
  call_booked: 'call_booked',
  follow_up: 'follow_up',
  prospect: 'prospect',
  customer: 'customer',
  no_show_not_reached: 'no_show',
};

const BOARD_LIST_FILTER: Record<ContactBoardColumn, LifecycleKey> = {
  new: 'new',
  contacted: 'contacted',
  call_booked: 'call_booked',
  follow_up: 'follow_up',
  prospect: 'prospect',
  customer: 'customer',
  no_show_not_reached: 'not_reached',
};

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function selectionLabel(count: number): string {
  return count === 1 ? '1 Kontakt gewählt' : `${count} Kontakte gewählt`;
}

function contactsText(count: number): string {
  return count === 1 ? '1 Kontakt' : `${formatInteger(count)} Kontakte`;
}

function sourceText(item: ContactListItem): string {
  return [item.source, item.campaign].filter((part): part is string => Boolean(part)).join(' · ') || '—';
}

function nextStepUrgent(item: ContactListItem): boolean {
  return item.nextStep.overdue || /fällig/i.test(item.nextStep.label ?? '');
}

interface BulkOutcome {
  tone: 'success' | 'warning' | 'danger';
  text: string;
  failures: { name: string; error: string }[];
}

function summarizeBulk(result: BulkResult, items: ContactListItem[], verb: string): BulkOutcome {
  const ok = result.results.filter((entry) => entry.ok).length;
  const failures = result.results
    .filter((entry) => !entry.ok)
    .map((entry) => {
      const item = items.find((candidate) => candidate.id === entry.id);
      return { name: item ? `${item.firstName} ${item.lastName}` : entry.id, error: entry.error ?? '' };
    });
  const text = `${verb} für ${ok} von ${result.results.length} Kontakten.`;
  return { tone: failures.length === 0 ? 'success' : ok === 0 ? 'danger' : 'warning', text, failures };
}

export interface ContactsPageProps {
  /** `/contacts/new`: the create dialog is open above the list. */
  createOpen?: boolean | undefined;
}

/** Kontakte 01–03 (list, selection, board) and 09 (mobile); `/contacts/new` opens Kontakte 04/05. */
export function ContactsPage({ createOpen = false }: ContactsPageProps) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const permissions = usePermissions();
  const workspace = useWorkspace();
  const mobile = useIsMobile();
  const compact = useIsCompact();
  const { toast } = useToast();

  const view = params.get('view') === 'board' ? 'board' : 'list';
  const lifecycle = params.get('lifecycle') ?? '';
  const partner = params.get('partner') ?? '';
  // „Einreicher" (owner decision 02.11.2026): only within a selected partner.
  const submitter = partner ? (params.get('submitter') ?? '') : '';
  const q = params.get('q') ?? '';
  const createdFrom = params.get('createdFrom') ?? '';
  const createdUntil = params.get('createdUntil') ?? '';
  // „Finanzierbar“ (D-381): column, sort and filter only for roles that see self-disclosures.
  const financingVisible = financingVisibleForRole(workspace.user.roleKey);
  const financingOnly = financingVisible && params.get('financing') === 'calculated';
  const [sort, setSort] = useState<ContactListSort>(readStoredSort);
  const [search, setSearch] = useState(q);
  const debouncedSearch = useDebouncedValue(search.trim(), 350);

  useEffect(() => {
    if (debouncedSearch === q) return;
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (debouncedSearch) next.set('q', debouncedSearch);
        else next.delete('q');
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, q, setParams]);

  function patchParams(changes: Record<string, string | null>) {
    setParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(changes)) {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
      }
      return next;
    });
  }

  const changeSort = useCallback((next: ContactListSort) => {
    setSort(next);
    storeSort(next);
  }, []);

  const range = useMemo(
    () => ({
      ...(createdFrom ? { createdFrom } : {}),
      ...(createdUntil ? { createdUntil } : {}),
    }),
    [createdFrom, createdUntil],
  );
  const financingFilter = useMemo(
    () => (financingOnly ? { financing: 'calculated' as const } : {}),
    [financingOnly],
  );
  const filter = useMemo(
    () => ({
      q,
      lifecycle,
      ...(partner ? { referrerPartnerId: partner } : {}),
      ...(submitter ? { referrerSubmitter: submitter } : {}),
      ...range,
      ...financingFilter,
    }),
    [q, lifecycle, partner, submitter, range, financingFilter],
  );
  const listSort: ContactListSort = !financingVisible && sort === 'financing' ? 'name' : sort;
  const list = useContactList(filter, listSort);
  const counts = useContactCounts(
    useMemo(
      () => ({
        q,
        ...(partner ? { referrerPartnerId: partner } : {}),
        ...(submitter ? { referrerSubmitter: submitter } : {}),
        ...range,
        ...financingFilter,
      }),
      [q, partner, submitter, range, financingFilter],
    ),
  );
  const partners = useReferrerPartnerOptions(permissions.has('contacts.view'));
  const submitters = useReferrerSubmitterOptions(partner);
  // The board ignores the sort and the lifecycle pills but respects the „Zeitraum" filter (FB-003).
  const board = useContactBoard(view === 'board', range);
  const items = useMemo(() => list.data?.pages.flatMap((page) => page.items) ?? [], [list.data]);
  const total = list.data?.pages[0]?.total ?? counts.data?.all ?? 0;

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    setSelected(new Set());
  }, [q, lifecycle, partner, submitter, view, createdFrom, createdUntil, financingOnly]);
  const selectedItems = useMemo(() => items.filter((item) => selected.has(item.id)), [items, selected]);
  const selecting = selected.size > 0;

  const [lifecycleDialog, setLifecycleDialog] = useState<{
    subjects: LifecycleSubject[];
    preselect: LifecycleTarget | undefined;
  } | null>(null);
  const [ownerDialog, setOwnerDialog] = useState(false);
  const [outcome, setOutcome] = useState<BulkOutcome | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'list' | 'export' | null>(null);

  const refresh = useCallback(async () => {
    await Promise.all([
      list.refetch(),
      counts.refetch(),
      view === 'board' ? board.refetch() : Promise.resolve(),
    ]);
  }, [board, counts, list, view]);

  const openRecord = useCallback((id: string) => void navigate(`/contacts/${id}`), [navigate]);

  const rowKey = useCallback((row: ContactListItem) => row.id, []);
  const columns = useMemo<TableColumn<ContactListItem>[]>(() => {
    const all: TableColumn<ContactListItem>[] = [
      {
        id: 'contact',
        header: 'Kontakt',
        track: 'minmax(0,1.4fr)',
        cell: (row) => (
          <>
            <span className={styles.avatarWrap}>
              <Avatar
                initials={row.initials}
                name={`${row.firstName} ${row.lastName}`}
                size={28}
                tone="sand"
              />
              {!selecting ? (
                <span className={styles.hoverCheck} onClick={(event) => event.stopPropagation()}>
                  <Checkbox
                    label="Auswählen"
                    labelHidden
                    checked={false}
                    onChange={() => setSelected(new Set([row.id]))}
                  />
                </span>
              ) : null}
            </span>
            <span className={styles.name}>
              {row.firstName} {row.lastName}
            </span>
          </>
        ),
      },
      {
        id: 'entry',
        header: <EntryHeader sort={listSort} onSort={changeSort} />,
        track: '108px',
        cell: (row) => <EntryCell createdAt={row.createdAt} />,
      },
      {
        id: 'lifecycle',
        header: 'Lifecycle',
        track: '140px',
        cell: (row) => <Pill variant={row.lifecycle.tone}>{row.lifecycle.label}</Pill>,
      },
      {
        id: 'deal',
        header: 'Deal',
        track: 'minmax(0,1fr)',
        cell: (row) => <span className={styles.muted}>{row.deal ?? '—'}</span>,
      },
      {
        id: 'owner',
        header: 'Besitzer',
        track: '120px',
        cell: (row) => <span className={styles.muted}>{row.owner ? row.owner.name : 'Dialer-Pool'}</span>,
      },
      {
        id: 'source',
        header: 'Quelle',
        track: 'minmax(0,1fr)',
        cell: (row) => <span className={styles.muted}>{sourceText(row)}</span>,
      },
      {
        id: 'financing',
        header: <FinancingHeader sort={listSort} onSort={changeSort} />,
        track: '132px',
        cell: (row) => <FinancingCell state={row.financingState} volumeUsedCents={row.volumeUsedCents} />,
      },
      {
        id: 'next',
        header: 'Nächster Schritt',
        track: 'minmax(0,1.2fr)',
        cell: (row) => (
          <span className={clsx(styles.nextStep, nextStepUrgent(row) && styles.nextStepUrgent)}>
            {row.nextStep.label ?? '—'}
          </span>
        ),
      },
    ];
    const visible = financingVisible ? all : all.filter((column) => column.id !== 'financing');
    // compact (≤ 1360): „Quelle" and „Eingang" give way to the remaining columns
    return compact ? visible.filter((column) => column.id !== 'source' && column.id !== 'entry') : visible;
  }, [changeSort, compact, financingVisible, listSort, selecting]);

  const pills = useMemo(() => {
    const byLifecycle = counts.data?.byLifecycle ?? {};
    return PILL_ORDER.filter((key) => key !== 'no_show' || (byLifecycle['no_show'] ?? 0) > 0).map((key) => ({
      key,
      label: lifecycleLabels[key],
      count: byLifecycle[key] ?? 0,
    }));
  }, [counts.data]);

  async function runBulk(action: 'add_to_list' | 'export') {
    const ids = selectedItems.map((item) => item.id);
    if (ids.length === 0) return;
    setActionError(null);
    setOutcome(null);
    setBusy(action === 'export' ? 'export' : 'list');
    try {
      if (action === 'export') {
        const name = await exportContacts({ ids });
        toast({ message: `${name} · ${contactsText(ids.length)} exportiert.` });
      } else {
        const result = await bulkContacts({ ids, action: 'add_to_list' });
        setOutcome(summarizeBulk(result, items, 'Zur Liste hinzugefügt'));
      }
    } catch (error) {
      setActionError(errorMessage(error));
    } finally {
      setBusy(null);
    }
  }

  function openLifecycleForSelection() {
    setLifecycleDialog({
      subjects: selectedItems.map((item) => ({
        id: item.id,
        name: `${item.firstName} ${item.lastName}`,
        lifecycle: item.lifecycle,
        owner: knownFromRef(item.owner)[0] ?? null,
      })),
      preselect: undefined,
    });
  }

  function onBoardMove(card: BoardCard, from: ContactBoardColumn, to: ContactBoardColumn | null) {
    // A single-stage column names the card's stage, so the dialog leaves it out of the targets.
    const current: LifecycleKey | null = from === 'no_show_not_reached' ? null : from;
    setLifecycleDialog({
      subjects: [
        {
          id: card.id,
          name: card.name,
          lifecycle: current
            ? { key: current, label: lifecycleLabels[current], tone: lifecycleTones[current] }
            : null,
          owner: null,
        },
      ],
      preselect: to ? BOARD_TARGETS[to] : undefined,
    });
  }

  const canCreate = permissions.has('contacts.create');
  const canExport = permissions.has('contacts.export');
  const canChangeOwner = canChangeContactOwner(workspace.user.roleKey);
  const noContactsAtAll =
    counts.isSuccess &&
    counts.data.all === 0 &&
    q === '' &&
    lifecycle === '' &&
    partner === '' &&
    createdFrom === '' &&
    createdUntil === '' &&
    !financingOnly;
  // „Tippgeber-Partner" filter (D-248): shown once the organization has a partner or a filter is set.
  const partnerOptions = partners.data ?? [];
  const showPartnerFilter = view === 'list' && (partnerOptions.length > 0 || partner !== '');
  const submitterOptions = submitters.data ?? [];

  if (noContactsAtAll && !createOpen) return <ModuleEmptyPage module="contacts" />;

  const bulkActions = (
    <>
      <Button
        variant="ghostInverse"
        size="small"
        loading={busy === 'list'}
        onClick={() => void runBulk('add_to_list')}
      >
        Zu Liste hinzufügen
      </Button>
      {canChangeOwner ? (
        <Button variant="ghostInverse" size="small" onClick={() => setOwnerDialog(true)}>
          Besitzer ändern
        </Button>
      ) : null}
      <Button variant="ghostInverse" size="small" onClick={openLifecycleForSelection}>
        Lifecycle ändern
      </Button>
      {canExport ? (
        <Button
          variant="ghostInverse"
          size="small"
          loading={busy === 'export'}
          onClick={() => void runBulk('export')}
        >
          Exportieren
        </Button>
      ) : null}
    </>
  );

  const tracks = columns.map((column) => column.track ?? 'minmax(0,1fr)');

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div className={styles.headText}>
          <h1 className={styles.title}>Kontakte</h1>
          <div className={styles.count}>{contactsText(total)}</div>
        </div>
        <div className={styles.headActions}>
          {mobile && canCreate ? (
            <div className={styles.mobileHead}>
              <Button onClick={() => void navigate('/contacts/new')}>Anlegen</Button>
            </div>
          ) : null}
          <SearchField
            label="Kontakte durchsuchen"
            placeholder="Name, Telefon, E-Mail"
            icon={false}
            className={styles.search}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {showPartnerFilter ? (
            <Select
              label="Tippgeber-Partner"
              labelHidden
              size="compact"
              className={styles.partnerFilter}
              value={partner}
              options={[
                { value: '', label: 'Alle Tippgeber-Partner' },
                ...partnerOptions.map((option) => ({ value: option.id, label: option.name })),
                ...(partner !== '' && !partnerOptions.some((option) => option.id === partner)
                  ? [{ value: partner, label: 'Tippgeber-Partner (inaktiv)' }]
                  : []),
              ]}
              onChange={(event) => patchParams({ partner: event.target.value || null, submitter: null })}
            />
          ) : null}
          {showPartnerFilter && partner !== '' && (submitterOptions.length > 0 || submitter !== '') ? (
            <Select
              label="Einreicher"
              labelHidden
              size="compact"
              className={styles.partnerFilter}
              value={submitter}
              options={[
                { value: '', label: 'Alle Einreicher' },
                ...submitterOptions.map((option) => ({ value: option.key, label: option.name })),
                ...(submitter !== '' && !submitterOptions.some((option) => option.key === submitter)
                  ? [{ value: submitter, label: submitter }]
                  : []),
              ]}
              onChange={(event) => patchParams({ submitter: event.target.value || null })}
            />
          ) : null}
          {!mobile ? (
            <Segmented
              label="Ansicht"
              value={view}
              items={[
                { value: 'list', label: 'Liste' },
                { value: 'board', label: 'Board' },
              ]}
              onChange={(value) => patchParams({ view: value === 'board' ? 'board' : null })}
            />
          ) : null}
          {!mobile && permissions.tool('dialer') === 'ok' ? (
            // D-442: the list overview (smart views, campaigns, imports) next to the view switch
            <Button variant="secondary" onClick={() => void navigate('/dialer/lists')}>
              Listen
            </Button>
          ) : null}
          {!mobile && canCreate ? (
            <Button onClick={() => void navigate('/contacts/new')}>Kontakt anlegen</Button>
          ) : null}
        </div>
      </div>

      <div className={styles.filterRow}>
        {view === 'list' || mobile ? (
          <div className={styles.pills} role="group" aria-label="Lifecycle-Filter">
            <FilterChip
              label="Alle"
              count={formatInteger(counts.data?.all ?? 0)}
              active={lifecycle === ''}
              onClick={() => patchParams({ lifecycle: null })}
            />
            {pills.map((pill) => (
              <FilterChip
                key={pill.key}
                label={pill.label}
                count={formatInteger(pill.count)}
                active={lifecycle === pill.key}
                onClick={() => patchParams({ lifecycle: lifecycle === pill.key ? null : pill.key })}
              />
            ))}
          </div>
        ) : null}
        <div className={styles.listControls}>
          {financingVisible && (view === 'list' || mobile) ? (
            <FilterChip
              label="Finanzierbar"
              active={financingOnly}
              onClick={() => patchParams({ financing: financingOnly ? null : 'calculated' })}
            />
          ) : null}
          <EntryRangeFilter
            value={{ from: createdFrom, until: createdUntil }}
            onChange={(next) =>
              patchParams({ createdFrom: next.from || null, createdUntil: next.until || null })
            }
          />
          {view === 'list' && !mobile ? (
            <div className={styles.sort}>
              <span>Sortiert nach</span>
              <Select
                label="Sortierung"
                labelHidden
                size="compact"
                className={styles.sortSelect}
                value={listSort}
                options={
                  financingVisible
                    ? SORT_OPTIONS
                    : SORT_OPTIONS.filter((option) => option.value !== 'financing')
                }
                onChange={(event) => changeSort(event.target.value as ContactListSort)}
              />
            </div>
          ) : null}
        </div>
      </div>

      {actionError ? (
        <Banner tone="danger" className={styles.banner}>
          {actionError}
        </Banner>
      ) : null}
      {outcome ? (
        <Banner tone={outcome.tone} className={styles.banner}>
          {outcome.text}
          {outcome.failures.length > 0 ? (
            <ul className={styles.resultList}>
              {outcome.failures.map((failure) => (
                <li key={failure.name}>
                  {failure.name}: {failure.error}
                </li>
              ))}
            </ul>
          ) : null}
        </Banner>
      ) : null}

      {view === 'board' && !mobile ? (
        board.isError ? (
          <Banner tone="danger">{errorMessage(board.error)}</Banner>
        ) : board.data ? (
          <ContactsBoard
            columns={board.data.columns}
            mobile={false}
            onOpen={openRecord}
            onMove={onBoardMove}
            onMore={(column) => patchParams({ view: null, lifecycle: BOARD_LIST_FILTER[column] })}
          />
        ) : (
          <TableSkeleton columns={6} rows={2} headerTone="plain" />
        )
      ) : list.isError ? (
        <Banner
          tone="danger"
          actions={
            <Button variant="secondary" size="small" onClick={() => void list.refetch()}>
              Erneut laden
            </Button>
          }
        >
          {errorMessage(list.error)}
        </Banner>
      ) : list.isPending ? (
        mobile ? (
          <TableSkeleton columns={['minmax(0,1fr)']} rows={7} headerTone="plain" avatar />
        ) : (
          <Card flush className={styles.tableCard}>
            <TableSkeleton columns={tracks} rows={9} headerTone="plain" avatar />
          </Card>
        )
      ) : mobile ? (
        <div className={styles.mobileList}>
          {items.length === 0 ? <div className={styles.mobileEmpty}>Keine Kontakte gefunden.</div> : null}
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              className={styles.mobileRow}
              onClick={() => openRecord(item.id)}
            >
              <Avatar
                initials={item.initials}
                name={`${item.firstName} ${item.lastName}`}
                size={36}
                tone="sand"
              />
              <span className={styles.mobileText}>
                <span className={styles.mobileTitleRow}>
                  <span className={styles.mobileName}>
                    {item.firstName} {item.lastName}
                  </span>
                  <Pill variant={item.lifecycle.tone}>{item.lifecycle.label}</Pill>
                </span>
                <span className={clsx(styles.mobileSub, nextStepUrgent(item) && styles.nextStepUrgent)}>
                  {item.nextStep.label ?? '—'}
                </span>
              </span>
            </button>
          ))}
        </div>
      ) : (
        <>
          <Card flush className={styles.tableCard}>
            <Table
              label="Kontakte"
              columns={columns}
              rows={items}
              rowKey={rowKey}
              headerTone="plain"
              onRowClick={(row) => openRecord(row.id)}
              selectable={selecting}
              selected={selected}
              onSelectionChange={setSelected}
              selectionLabel={selectionLabel}
              bulkActions={bulkActions}
              emptyText="Keine Kontakte gefunden."
            />
          </Card>
          {selected.size > 0 ? (
            <p className={styles.selectionNote}>
              Löschen gibt es nicht als Sammelaktion. DSGVO-Löschung läuft einzeln über „Sperren“ und eine
              Admin-Task.
            </p>
          ) : null}
        </>
      )}

      {(view === 'list' || mobile) && list.hasNextPage ? (
        <div className={styles.more}>
          <Button
            variant="secondary"
            loading={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
          >
            Weitere laden
          </Button>
        </div>
      ) : null}

      <CreateContactDialog
        open={createOpen}
        onClose={() =>
          void navigate({ pathname: '/contacts', search: params.toString() ? `?${params.toString()}` : '' })
        }
        onOpenExisting={openRecord}
        onCreated={(created) => {
          void refresh();
          void navigate(
            created.next === 'book_appointment'
              ? `/contacts/${created.id}?editor=appointment`
              : `/contacts/${created.id}`,
          );
        }}
      />

      {lifecycleDialog ? (
        <LifecycleDialog
          open
          subjects={lifecycleDialog.subjects}
          preselect={lifecycleDialog.preselect}
          onClose={() => setLifecycleDialog(null)}
          onDone={(result) => {
            setLifecycleDialog(null);
            if (result.bulk) {
              setOutcome(summarizeBulk(result.bulk, items, 'Lifecycle geändert'));
            } else {
              toast({ message: `Lifecycle geändert: ${result.single?.lifecycle.label ?? ''}.` });
            }
            setSelected(new Set());
            void refresh();
          }}
        />
      ) : null}

      <AssignDialog
        open={ownerDialog}
        kind="owner"
        subjects={selectedItems.map((item) => ({
          id: item.id,
          name: `${item.firstName} ${item.lastName}`,
          owner: knownFromRef(item.owner)[0] ?? null,
        }))}
        onClose={() => setOwnerDialog(false)}
        onDone={(result) => {
          setOwnerDialog(false);
          if (result.bulk) setOutcome(summarizeBulk(result.bulk, items, 'Besitzer geändert'));
          setSelected(new Set());
          void refresh();
        }}
      />
    </div>
  );
}
